import { DayPicker, PropsBase as ReactDayPickerBaseProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { startOfMonth, endOfMonth, formatMonth, formatWeekdayAbbreviation } from '@gloat/time';
import { Div } from '../box';
import { DatePickerStyles, CustomDropdown, CustomMonthDropdown, CustomYearsDropdown } from './date-picker-components';

export interface DatePickerProps {
  mode?: 'single' | 'single-month-year';
  value?: Date;
  onChange?: (value: Date) => void;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
}

const DatePicker = (props: DatePickerProps) => {
  // `DayPicker` has really strange and complex `required` typing, not sure how to fix it and Claude Code also :)
  const { required: requiredProp, mode, minDate: minDateProp, maxDate: maxDateProp } = props;
  const required = requiredProp as true;

  const monthYearOnly = mode === 'single-month-year';

  const minDate = minDateProp ?? new Date();
  const maxDate = maxDateProp ?? new Date(2099, 11, 31);

  let disabled: ReactDayPickerBaseProps['disabled'] = undefined;
  if (minDate) {
    disabled = { before: minDate };
  }
  if (maxDate) {
    disabled = { ...disabled, after: maxDate };
  }

  const { value, onChange } = props;
  const onSelectHandler = (date: Date | undefined) => {
    if (onChange && date) {
      onChange(date);
    }
  };

  return (
    <div aria-required={required} data-testid={'date-picker'}>
      <DatePickerStyles />

      <DayPicker
        animate
        mode={'single'}
        required={required}
        captionLayout={monthYearOnly ? 'dropdown' : 'dropdown-years'}
        defaultMonth={value}
        selected={value}
        onSelect={onSelectHandler}
        onMonthChange={newMonth => {
          if (monthYearOnly) {
            onChange?.(new Date(newMonth.getFullYear(), newMonth.getMonth(), 1));
          }
        }}
        components={{
          Dropdown: CustomDropdown,
          YearsDropdown: CustomYearsDropdown,
          MonthsDropdown: CustomMonthDropdown,
        }}
        disabled={disabled}
        startMonth={minDate ? startOfMonth(minDate) : undefined}
        endMonth={maxDate ? endOfMonth(maxDate) : undefined}
        locale={undefined} // We override formatting not to conflict with `react-day-picker`'s `date-fns` way of i18n
        formatters={{
          formatMonthDropdown: date => formatMonth(date),
          formatWeekdayName: date => formatWeekdayAbbreviation(date),
        }}
        hideNavigation={monthYearOnly}
        styles={monthYearOnly ? { month_grid: { display: 'none' } } : undefined}
        footer={monthYearOnly ? <Div height={140} /> : undefined}
      />
    </div>
  );
};

export default DatePicker;
